import React, { memo } from 'react';

const keyframeCSS = `
  @keyframes studioSpotlight1 {
    0%, 100% { opacity: 0.12; transform: translate(-50%, -40%) rotate(-15deg) scale(1); }
    33% { opacity: 0.2; transform: translate(-30%, -35%) rotate(-10deg) scale(1.1); }
    66% { opacity: 0.08; transform: translate(-60%, -45%) rotate(-20deg) scale(0.95); }
  }
  @keyframes studioSpotlight2 {
    0%, 100% { opacity: 0.1; transform: translate(50%, -40%) rotate(15deg) scale(1); }
    50% { opacity: 0.18; transform: translate(40%, -35%) rotate(10deg) scale(1.05); }
  }
  @keyframes studioSpotlight3 {
    0%, 100% { opacity: 0.06; transform: translate(0%, -30%) rotate(0deg); }
    50% { opacity: 0.14; transform: translate(5%, -35%) rotate(2deg); }
  }
  @keyframes studioParticle {
    0% { transform: translateY(0) translateX(0) scale(1); opacity: 0; }
    10% { opacity: 1; }
    90% { opacity: 1; }
    100% { transform: translateY(var(--py, -200px)) translateX(var(--px, 30px)) scale(0.3); opacity: 0; }
  }
  @keyframes stageFloorGlow {
    0%, 100% { opacity: 0.3; }
    50% { opacity: 0.6; }
  }
  @keyframes studioLensFlare {
    0%, 100% { opacity: 0; transform: scale(0.5); }
    50% { opacity: 0.15; transform: scale(1); }
  }
`;

const StudioBackground = memo(function StudioBackground({ intensity = 'normal' }) {
  const isHigh = intensity === 'high';

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      pointerEvents: 'none',
      zIndex: 0,
      overflow: 'hidden',
    }}>
      <style>{keyframeCSS}</style>

      {/* Deep studio background gradient */}
      <div style={{
        position: 'absolute',
        inset: 0,
        background: `
          radial-gradient(ellipse 120% 60% at 50% 0%, rgba(10,15,40,0.95) 0%, transparent 70%),
          radial-gradient(ellipse 80% 50% at 50% 100%, rgba(139,69,19,0.06) 0%, transparent 50%),
          radial-gradient(ellipse 60% 40% at 20% 20%, rgba(0,50,120,0.08) 0%, transparent 50%),
          radial-gradient(ellipse 60% 40% at 80% 20%, rgba(0,50,120,0.08) 0%, transparent 50%),
          linear-gradient(180deg, #020208 0%, #0a0a1a 30%, #0f0f2a 60%, #080814 100%)
        `,
      }} />

      {/* Spotlight 1 - Left warm */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: '10%',
        width: '50%',
        height: '80%',
        background: 'radial-gradient(ellipse 40% 60% at center, rgba(212,175,55,0.08) 0%, rgba(200,150,50,0.02) 40%, transparent 70%)',
        animation: 'studioSpotlight1 8s ease-in-out infinite',
        filter: 'blur(30px)',
      }} />

      {/* Spotlight 2 - Right cool */}
      <div style={{
        position: 'absolute',
        top: 0,
        right: '10%',
        width: '50%',
        height: '80%',
        background: 'radial-gradient(ellipse 40% 60% at center, rgba(100,150,255,0.06) 0%, rgba(50,100,200,0.02) 40%, transparent 70%)',
        animation: 'studioSpotlight2 10s ease-in-out infinite',
        filter: 'blur(30px)',
      }} />

      {/* Spotlight 3 - Center gold */}
      <div style={{
        position: 'absolute',
        top: '5%',
        left: '30%',
        width: '40%',
        height: '60%',
        background: 'radial-gradient(ellipse at center, rgba(212,175,55,0.05) 0%, transparent 60%)',
        animation: 'studioSpotlight3 6s ease-in-out infinite',
        filter: 'blur(20px)',
      }} />

      {/* Stage floor reflection */}
      <div style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        height: '25%',
        background: 'linear-gradient(0deg, rgba(212,175,55,0.04) 0%, rgba(212,175,55,0.01) 40%, transparent 100%)',
        animation: 'stageFloorGlow 5s ease-in-out infinite',
      }} />

      {/* Floor line */}
      <div style={{
        position: 'absolute',
        bottom: '15%',
        left: '5%',
        right: '5%',
        height: '1px',
        background: 'linear-gradient(90deg, transparent, rgba(212,175,55,0.15), rgba(212,175,55,0.25), rgba(212,175,55,0.15), transparent)',
        opacity: 0.5,
      }} />

      {/* Lens flare effect */}
      {isHigh && (
        <div style={{
          position: 'absolute',
          top: '8%',
          left: '50%',
          width: '200px',
          height: '200px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, rgba(212,175,55,0.03) 30%, transparent 60%)',
          animation: 'studioLensFlare 7s ease-in-out infinite',
          transform: 'translate(-50%, 0)',
          filter: 'blur(10px)',
        }} />
      )}

      {/* Floating dust particles */}
      {Array.from({ length: isHigh ? 20 : 10 }, (_, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            left: `${5 + Math.random() * 90}%`,
            bottom: `${Math.random() * 60}%`,
            width: `${1 + Math.random() * 2}px`,
            height: `${1 + Math.random() * 2}px`,
            borderRadius: '50%',
            backgroundColor: i % 3 === 0 ? 'rgba(212,175,55,0.4)' : 'rgba(180,200,255,0.3)',
            animation: `studioParticle ${6 + Math.random() * 8}s ease-in-out infinite`,
            animationDelay: `${Math.random() * 8}s`,
            '--py': `${-100 - Math.random() * 200}px`,
            '--px': `${(Math.random() - 0.5) * 100}px`,
            filter: 'blur(0.5px)',
          }}
        />
      ))}

      {/* Vignette overlay */}
      <div style={{
        position: 'absolute',
        inset: 0,
        background: 'radial-gradient(ellipse 70% 60% at 50% 45%, transparent 0%, rgba(0,0,0,0.4) 100%)',
        pointerEvents: 'none',
      }} />
    </div>
  );
});

export default StudioBackground;
